import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { SearchEscalatedEvent } from '../interfaces/websocket/websocket-events.interface';

export { SearchEscalatedEvent };

export interface OrderCreatedEvent {
  orderId: number;
  clientId: number;
  pickupAddress: string;
  destinationAddress: string;
  estimatedCost: number;
  timestamp: string;
}

export interface OrderStatusChangeEvent {
  orderId: number;
  status: string;
  previousStatus: string;
  timestamp: string;
  // Backend sends these fields (fromStatus/toStatus as string codes)
  fromStatus?: string;
  toStatus?: string;
  companyId?: number;
  clientId?: number;
  driverId?: number;
  eventType?: string;
}

export interface DriverAssignmentEvent {
  orderId: number;
  driverId: number;
  eta: number;
  distance: number;
}

export interface TimerEvent {
  orderId: number;
  waitTime: number;
  threshold: number;
  timestamp: string;
}

export interface BatchTimerUpdateEvent {
  updates: Array<{ orderId: number; waitTime: number }>;
  warningOrders: number[];
  criticalOrders: number[];
}

export interface DispatcherStatusUpdateEvent {
  onlineCount: number;
  busyCount: number;
  totalCount: number;
  timestamp: string;
}

export interface TaxometerUpdateEvent {
  orderId: number;
  userId: number;
  distance: number;
  currentPrice: number;
  speed?: number;
  timestamp: string;
}

export interface LocationUpdateEvent {
  userId: number;
  latitude: number;
  longitude: number;
  heading?: number;
  speed?: number;
  timestamp: string;
  orderId?: number; // If present, driver is busy with this order
  companyId?: number;
  // Backend sends location as nested object, we flatten in the handler
  location?: {
    lat: number;
    lng: number;
    speed?: number;
    heading?: number;
    accuracy?: number;
  };
}

export interface DriverOnlineStatusEvent {
  driverId: number;
  userId: number;
  companyId: number;
  isOnline: boolean;
  lastLocation?: {
    lat: number;
    lng: number;
  };
  timestamp: string;
}

/**
 * WebSocketDispatcherService
 *
 * Manages WebSocket connection for real-time dispatcher updates
 * Features:
 * - Auto-reconnect with exponential backoff
 * - Room management (dispatcher-{companyId})
 * - Event streams via RxJS Subjects
 * - Connection status tracking
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketDispatcherService {
  private socket: Socket | null = null;
  private readonly wsUrl = environment.socketUrl || '';
  private readonly wsNamespace = '/ws'; // Backend WebSocket namespace

  // Connection status
  private connectionStatus$ = new BehaviorSubject<'connected' | 'disconnected' | 'connecting'>('disconnected');

  // Event streams
  // Session was revoked elsewhere - AuthService subscribes to this instead of
  // being injected here, which would create a circular dependency.
  private forceLogout$ = new Subject<{ reason?: string; message?: string }>();
  private newOrder$ = new Subject<OrderCreatedEvent>();
  private orderStatusChanged$ = new Subject<OrderStatusChangeEvent>();
  private driverAssigned$ = new Subject<DriverAssignmentEvent>();
  private timerWarning$ = new Subject<TimerEvent>();
  private timerCritical$ = new Subject<TimerEvent>();
  private dispatcherStatusUpdate$ = new Subject<DispatcherStatusUpdateEvent>();
  private batchTimerUpdate$ = new Subject<BatchTimerUpdateEvent>();
  private driverPositionUpdate$ = new Subject<LocationUpdateEvent>();
  private driverOnlineStatusChanged$ = new Subject<DriverOnlineStatusEvent>();
  private taxometerUpdate$ = new Subject<TaxometerUpdateEvent>();
  private searchEscalated$ = new Subject<SearchEscalatedEvent>();

  // WSS-006: emits after successful reconnect so consumers can refetch stale data
  private reconnect$ = new Subject<void>();

  // WSS-007: idempotency guard — deduplicates repeated WebSocket events by eventId
  private readonly seenEventIds = new Set<string>();
  private readonly EVENT_DEDUP_TTL_MS = 5 * 60 * 1000;

  // Reconnection settings
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = Infinity; // Never stop trying
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly reconnectDelays = [1000, 2000, 4000, 8000, 16000, 30000, 60000]; // Exponential backoff, max 60s
  private lastToken: string = '';
  private lastCompanyId: number = 0;
  // Set by disconnect() so the socket.io 'disconnect'/'connect_error' handlers
  // below know this was a deliberate client-side disconnect (logout,
  // force-logout) and must not schedule a reconnect. Without this, calling
  // socket.disconnect() itself fires 'disconnect', which unconditionally
  // called attemptReconnect() - so every logout immediately re-armed a
  // reconnect timer that kept hitting the backend with the now-invalid token.
  private intentionalDisconnect = false;

  constructor() {}

  /**
   * WSS-007: Returns true if this eventId was already processed within the TTL window.
   * Schedules automatic removal after TTL to prevent unbounded Set growth.
   */
  private isDuplicateEvent(eventId: string | undefined): boolean {
    if (!eventId) return false;
    if (this.seenEventIds.has(eventId)) return true;
    this.seenEventIds.add(eventId);
    setTimeout(() => this.seenEventIds.delete(eventId), this.EVENT_DEDUP_TTL_MS);
    return false;
  }

  /**
   * Update the stored token (e.g. after token refresh).
   * Next reconnect will use the new token.
   */
  updateToken(token: string): void {
    this.lastToken = token;
  }
  /**
   * Connect to WebSocket server
   */
  connect(token: string, companyId: number): void {
    if (this.socket?.connected) {
      return;
    }

    this.intentionalDisconnect = false;
    this.lastToken = token;
    this.lastCompanyId = companyId;
    this.connectionStatus$.next('connecting');
    // When wsUrl is empty, connect to /ws namespace on current origin (proxy handles routing)
    const fullUrl = this.wsUrl ? `${this.wsUrl}${this.wsNamespace}` : this.wsNamespace;

    // Destroy previous socket instance completely before creating new one
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.socket = io(fullUrl, {
      query: { token },
      transports: ['websocket', 'polling'],
      reconnection: false, // Manual reconnection with exponential backoff
    });

    this.setupConnectionHandlers(companyId);
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.intentionalDisconnect = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.connectionStatus$.next('disconnected');
    this.reconnectAttempts = 0;
    // Drop the cached token too - attemptReconnect() falls back to it when
    // localStorage has none, which otherwise kept retrying with a stale
    // (now server-rejected) token after logout.
    this.lastToken = '';
  }

  /**
   * Setup connection handlers (викликається ОДИН раз при створенні socket)
   */
  private setupConnectionHandlers(companyId: number): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      const wasReconnecting = this.reconnectAttempts > 0;
      this.connectionStatus$.next('connected');
      this.reconnectAttempts = 0;
      // WSS-006: notify components to refresh data after reconnect
      if (wasReconnecting) {
        this.reconnect$.next();
      }
    });

    // Listen for authenticated event
    this.socket.once('authenticated', (data: any) => {

      // Тепер підписуємось на події і join room
      this.setupEventListeners();
      this.joinDispatcherRoom(companyId);
    });

    this.socket.on('disconnect', (_reason: string) => {
      this.connectionStatus$.next('disconnected');

      // Deliberate client-side disconnect (logout, force-logout) - don't
      // reconnect. socket.disconnect() itself triggers this same event, so
      // without this guard every logout re-armed a reconnect loop.
      if (this.intentionalDisconnect) {
        return;
      }

      // Both "server disconnected us" and "network issue" cases should
      // auto-reconnect with backoff.
      this.attemptReconnect(companyId);
    });

    this.socket.on('connect_error', (_error: Error | Record<string, unknown>) => {
      this.connectionStatus$.next('disconnected');
      if (this.intentionalDisconnect) {
        return;
      }
      this.attemptReconnect(companyId);
    });

    // Session revoked by a newer login of the same account. Registered here
    // (not in setupEventListeners) so it is caught even before 'authenticated'.
    this.socket.on('force-logout', (data: { reason?: string; message?: string }) => {
      this.forceLogout$.next(data || {});
      this.disconnect();
    });
  }

  /**
   * Setup event listeners (викликається ПІСЛЯ authenticated)
   */
  private setupEventListeners(): void {
    if (!this.socket) return;


    this.socket.on('order-created', (data: OrderCreatedEvent & { eventId?: string }) => {
      if (this.isDuplicateEvent(data.eventId)) return;
      this.newOrder$.next(data);
    });

    this.socket.on('order-status-changed', (data: OrderStatusChangeEvent) => {
      this.orderStatusChanged$.next(data);
    });

    this.socket.on('order-search-started', (data: any) => {
      // Можна обробити як status change
      this.orderStatusChanged$.next({
        orderId: data.orderId,
        status: 'searching',
        previousStatus: 'draft',
        timestamp: data.timestamp,
      });
    });

    this.socket.on('order-cancelled', (data: any) => {
      // Обробити як status change з правильним статусом з data
      this.orderStatusChanged$.next({
        orderId: data.orderId,
        status: data.status || data.cancelledBy || 'cancelled_by_system',
        previousStatus: data.fromStatus || data.previousStatus || 'searching',
        timestamp: data.timestamp,
      });
    });

    this.socket.on('driver-assigned', (data: DriverAssignmentEvent) => {
      this.driverAssigned$.next(data);
    });

    // Backend broadcasts via UniversalBroadcastService: eventPrefix('order') + action('assigned') = 'order-assigned'
    this.socket.on('order-assigned', (data: any) => {
      this.driverAssigned$.next({
        orderId: data.orderId,
        driverId: data.driverId,
        eta: data.eta || 0,
        distance: data.distance || 0,
      });
    });

    // Backend broadcasts via BroadcastAction.DRIVER_ASSIGNED = 'order-driver-assigned'
    this.socket.on('order-driver-assigned', (data: any) => {
      this.driverAssigned$.next({
        orderId: data.orderId,
        driverId: data.driverId,
        eta: data.eta || 0,
        distance: data.distance || 0,
      });
    });

    this.socket.on('order-timer-warning', (data: TimerEvent) => {
      this.timerWarning$.next(data);
    });

    this.socket.on('order-timer-critical', (data: TimerEvent) => {
      this.timerCritical$.next(data);
    });

    this.socket.on('company-dispatcher-status-update', (data: DispatcherStatusUpdateEvent) => {
      this.dispatcherStatusUpdate$.next(data);
    });

    this.socket.on('order-batch-timer-update', (data: BatchTimerUpdateEvent) => {
      this.batchTimerUpdate$.next(data);
    });

    // Backend: EntityType.DRIVER + BroadcastAction.LOCATION_UPDATE = 'driver-location-update'
    this.socket.on('driver-location-update', (data: any) => {
      // Backend sends location as nested object { location: { lat, lng, speed, heading } }
      // Flatten to LocationUpdateEvent format
      const event: LocationUpdateEvent = {
        userId: data.userId,
        latitude: data.location?.lat ?? data.latitude ?? 0,
        longitude: data.location?.lng ?? data.longitude ?? 0,
        heading: data.location?.heading ?? data.heading,
        speed: data.location?.speed ?? data.speed,
        timestamp: data.timestamp,
        orderId: data.orderId,
        companyId: data.companyId,
      };
      this.driverPositionUpdate$.next(event);
    });

    // Also listen for order-location-update (when driver has active order)
    this.socket.on('order-location-update', (data: any) => {
      const event: LocationUpdateEvent = {
        userId: data.userId,
        latitude: data.location?.lat ?? data.latitude ?? 0,
        longitude: data.location?.lng ?? data.longitude ?? 0,
        heading: data.location?.heading ?? data.heading,
        speed: data.location?.speed ?? data.speed,
        timestamp: data.timestamp,
        orderId: data.orderId,
        companyId: data.companyId,
      };
      this.driverPositionUpdate$.next(event);
    });

    // Driver online/offline status change
    // Backend emits 'driver-status-changed' via UniversalBroadcastService
    // (EntityType.DRIVER + action 'STATUS_CHANGED' = 'driver-status-changed')
    this.socket.on('driver-status-changed', (data: any) => {
      const event: DriverOnlineStatusEvent = {
        driverId: data.driverId ?? data.userId,
        userId: data.userId ?? data.driverId,
        companyId: data.companyId,
        isOnline: data.isOnline ?? data.data?.isOnline ?? false,
        lastLocation: data.lastLocation ?? data.data?.lastLocation,
        timestamp: data.timestamp || new Date().toISOString(),
      };
      this.driverOnlineStatusChanged$.next(event);
    });

    // Also listen for legacy event name (if any older code emits it)
    this.socket.on('driver-online-status-changed', (data: DriverOnlineStatusEvent) => {
      this.driverOnlineStatusChanged$.next(data);
    });

    // Taxometer updates (in-progress orders)
    this.socket.on('order-taxometer-updated', (data: TaxometerUpdateEvent) => {
      this.taxometerUpdate$.next(data);
    });

    // WSS-002: search escalation events
    // New event name from backend: entityType=ORDER + action=SEARCH_ESCALATED → 'order-search-escalated'
    this.socket.on('order-search-escalated', (data: SearchEscalatedEvent) => {
      if (this.isDuplicateEvent(data.eventId)) return;
      this.searchEscalated$.next(data);
    });

    // Legacy alias — deprecated, kept for backward compat during rollout
    this.socket.on('order-search-level-changed', (data: SearchEscalatedEvent) => {
      if (this.isDuplicateEvent(data.eventId)) return;
      this.searchEscalated$.next(data);
    });

    // Room events
    this.socket.on('dispatcher-room-joined', (data: { room: string }) => {
    });
  }

  /**
   * Attempt to reconnect with exponential backoff.
   * Creates a fresh socket each time (old socket may have stale token/state).
   */
  private attemptReconnect(_companyId: number): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    const delayIndex = Math.min(this.reconnectAttempts, this.reconnectDelays.length - 1);
    const delay = this.reconnectDelays[delayIndex];

    this.reconnectTimeout = setTimeout(() => {
      if (this.intentionalDisconnect) {
        return;
      }
      this.reconnectAttempts++;
      // Always use the current localStorage token (may have been refreshed
      // since the last connect) - never fall back to this.lastToken here:
      // that field is a stale in-memory copy and is exactly what caused
      // reconnect attempts to keep firing with an already-invalidated
      // token after logout, before this method also started checking
      // intentionalDisconnect above.
      const freshToken = localStorage.getItem('access_token');
      if (!freshToken) {
        // No token available - user likely logged out, stop reconnecting
        return;
      }
      this.connect(freshToken, this.lastCompanyId);
    }, delay);
  }

  /**
   * Join dispatcher room
   */
  private joinDispatcherRoom(companyId: number): void {
    if (!this.socket?.connected) return;


    // Backend handler НЕ приймає payload, бере companyId з client.data
    this.socket.emit('join-dispatcher-company-room', (response: any) => {
      if (response?.success) {
      } else {
      }
    });
  }

  /**
   * Get connection status observable
   */
  getConnectionStatus(): Observable<'connected' | 'disconnected' | 'connecting'> {
    return this.connectionStatus$.asObservable();
  }

  /**
   * Event observables
   */
  onForceLogout(): Observable<{ reason?: string; message?: string }> {
    return this.forceLogout$.asObservable();
  }

  onNewOrder(): Observable<OrderCreatedEvent> {
    return this.newOrder$.asObservable();
  }

  onOrderStatusChanged(): Observable<OrderStatusChangeEvent> {
    return this.orderStatusChanged$.asObservable();
  }

  onDriverAssigned(): Observable<DriverAssignmentEvent> {
    return this.driverAssigned$.asObservable();
  }

  onTimerWarning(): Observable<TimerEvent> {
    return this.timerWarning$.asObservable();
  }

  onTimerCritical(): Observable<TimerEvent> {
    return this.timerCritical$.asObservable();
  }

  onDispatcherStatusUpdate(): Observable<DispatcherStatusUpdateEvent> {
    return this.dispatcherStatusUpdate$.asObservable();
  }

  onBatchTimerUpdate(): Observable<BatchTimerUpdateEvent> {
    return this.batchTimerUpdate$.asObservable();
  }

  onDriverPositionUpdate(): Observable<LocationUpdateEvent> {
    return this.driverPositionUpdate$.asObservable();
  }

  onDriverOnlineStatusChanged(): Observable<DriverOnlineStatusEvent> {
    return this.driverOnlineStatusChanged$.asObservable();
  }

  onTaxometerUpdate(): Observable<TaxometerUpdateEvent> {
    return this.taxometerUpdate$.asObservable();
  }

  // Alias for convenience
  onDriverStatusChanged(): Observable<DriverOnlineStatusEvent> {
    return this.driverOnlineStatusChanged$.asObservable();
  }

  // WSS-002: search escalation observable (new unified event replacing search-level-changed)
  onSearchEscalated(): Observable<SearchEscalatedEvent> {
    return this.searchEscalated$.asObservable();
  }

  // WSS-006: observable for components to react to reconnection by refetching data
  onReconnect(): Observable<void> {
    return this.reconnect$.asObservable();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}
